import Link from 'next/link'
import { Card } from '@/components/ui/card'
import { FIRST_STEP_ALTERNATIVE, type FirstStep } from '@/lib/onboarding/first-step'

/**
 * A student's first step (src/lib/onboarding/first-step.ts): what it is, how
 * long it takes and why, with the full-length SAT as the alternative when the
 * step is the Checkpoint. The same card on every page that shows it, so the
 * first step reads the same everywhere.
 *
 * Renders nothing for `step === null` (the student has a starting level), so
 * callers pass firstStep(...) straight in. No hooks and no server-only
 * imports: safe in server and client trees.
 */
export default function FirstStepCard({
  step,
  className,
  showAlternative = true,
}: {
  step: FirstStep | null
  className?: string
  /** False where the full SAT is already the page's own alternative (/sat),
   *  so the card doesn't link to the page it is on. */
  showAlternative?: boolean
}) {
  if (!step) return null
  const alternative = showAlternative && step.reason === 'checkpoint'

  return (
    <Card
      className={`border-rose-500/30 bg-gradient-to-r from-rose-500/10 to-pink-500/10 p-6 ${className ?? ''}`}
      data-testid="first-step"
    >
      <p className="text-xs font-semibold uppercase tracking-wide text-rose-700 dark:text-rose-300">
        Start here
      </p>
      <div className="mt-1 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-lg font-semibold text-foreground">{step.title}</h2>
          <p className="mt-1 text-sm font-medium text-foreground">{step.duration}</p>
          <p className="mt-1 text-sm text-muted-foreground">{step.purpose}</p>
        </div>
        <Link
          href={step.href}
          className="inline-flex h-11 shrink-0 items-center justify-center rounded-md bg-rose-600 px-6 text-sm font-semibold text-white transition-colors hover:bg-rose-700"
        >
          {step.cta}
        </Link>
      </div>
      {alternative && (
        <p className="mt-4 border-t border-rose-500/20 pt-3 text-sm text-muted-foreground">
          {FIRST_STEP_ALTERNATIVE.title} {FIRST_STEP_ALTERNATIVE.duration}.{' '}
          <Link
            href={FIRST_STEP_ALTERNATIVE.href}
            className="font-medium text-foreground underline underline-offset-2 hover:opacity-80"
          >
            {FIRST_STEP_ALTERNATIVE.cta}
          </Link>
        </p>
      )}
    </Card>
  )
}
